import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { groupApi } from '@/store/services/user-groups.service'; 
import {
    useGetGroupQuery,
    useCreateGroupMutation,
    useUpdateGroupMutation,
    useGetGroupUsersMutation,
    useAddUserToGroupMutation,
    useRemoveUserFromGroupMutation,
    useAddPermissionMutation,
    useRemovePermissionMutation 
} from '@/store/services/user-groups.service';
import { useGetCompaniesQuery } from '@/store/services/company.service';
import { useGetOfficesQuery } from '@/store/services/office.service';
import { useGetAttachmentTypesQuery } from '@/store/services/attachment-type.service';
import { Button } from '@material-tailwind/react';
import { Switch, Typography } from '@material-tailwind/react';
import { GroupForm, UserAssignment } from './components';
import ModelPermissionAssignment from './components/model-permission-assignment';
import PermissionEntitiesSidebar from './components/permission-entities-sidebar';
import { ENTITY_MAP } from '@/constants/permission-entities';

const QUERY_HOOKS = {
  useGetCompaniesQuery: useGetCompaniesQuery,
  useGetOfficesQuery: useGetOfficesQuery,
  useGetAttachmentTypesQuery: useGetAttachmentTypesQuery
};


const GroupDetail = () => {
    const dispatch = useDispatch();
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditing = !!id;

    const { data: group, error, isLoading: isGroupLoading } = useGetGroupQuery(id, { 
        skip: !id,
        refetchOnMountOrArgChange: false,
        refetchOnFocus: false,
        refetchOnReconnect: false
      });
    const [createGroup] = useCreateGroupMutation();
    const [updateGroup] = useUpdateGroupMutation();
    const [getGroupUsers] = useGetGroupUsersMutation();
    const [addUserToGroup] = useAddUserToGroupMutation();
    const [removeUserFromGroup] = useRemoveUserFromGroupMutation();

    const [formData, setFormData] = useState({ name: '', description: '' });
    const [errorMessage, setErrorMessage] = useState('');
    const [assignedUsers, setAssignedUsers] = useState([]);
    const [availableUsers, setAvailableUsers] = useState([]);
    const [searchAvailable, setSearchAvailable] = useState('');
    const [searchAssigned, setSearchAssigned] = useState('');
    const [activeStatus, setActiveStatus] = useState(false);
    const [selectedEntity, setSelectedEntity] = useState('company');

    const permissionEntities = Object.keys(ENTITY_MAP).map((key) => {
      const entityConfig = ENTITY_MAP[key];
      return {
          type: key,
          label: entityConfig.label,
          component: (
              <ModelPermissionAssignment
                  key={key}
                  groupId={id}
                  permissibleType={key}
                  title={entityConfig.label}
                  useGetItemsQuery={QUERY_HOOKS[entityConfig.queryHook]}
                  dataKey={entityConfig.dataKey}
              />
          )
      };
  });

    useEffect(() => {
        if (error && (error.status === 404 || error.originalStatus === 404)) {
        // Limpiar queries relacionadas
        dispatch(groupApi.util.invalidateTags([
            { type: 'Group', id },
            { type: 'GroupPermissions', id },
            { type: 'GroupUsers', id }
        ]));
        navigate('/users/groups', { replace: true });
        }
    }, [error, id, navigate, dispatch]);

    useEffect(() => {
        if (group) {
            setFormData({ 
                name: group.name, 
                description: group.description 
            });
            setActiveStatus(group.active);
        }
    }, [group]);

    useEffect(() => {
        const loadUsers = async () => {
            if (isEditing && id) {
                try {
                    const [assignedResponse, availableResponse] = await Promise.all([
                        getGroupUsers({ 
                            groupId: id, 
                            search: searchAssigned, 
                            assigned: true 
                        }).unwrap(),
                        getGroupUsers({ 
                            groupId: id, 
                            search: searchAvailable, 
                            assigned: false 
                        }).unwrap()
                    ]);
                    
                    setAssignedUsers(assignedResponse.groups || []);
                    setAvailableUsers(availableResponse.groups || []);
                } catch (err) {
                    if (err.status === 404) {
                        dispatch(groupApi.util.invalidateTags([{ type: 'Group', id }]));
                        navigate('/users/groups', { replace: true });
                    }
                }
            }
        };
        
        const debounceTimer = setTimeout(loadUsers, 300);
        return () => clearTimeout(debounceTimer);
    }, [id, isEditing, searchAssigned, searchAvailable, navigate, dispatch]);

      useEffect(() => {
        return () => {
          dispatch(groupApi.util.resetApiState());

          dispatch(groupApi.util.invalidateTags([
            { type: 'Group', id },
            { type: 'GroupUsers', id },
            { type: 'GroupPermissions', id }
          ]));
        };
      }, [dispatch, id]);

    const handleStatusChange = async () => {
        try {
            const newStatus = !activeStatus;
            await updateGroup({ id, active: newStatus }).unwrap();
            setActiveStatus(newStatus);
        } catch (err) {
            console.error('Error actualizando estado:', err);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isEditing) {
                await updateGroup({ id, ...formData }).unwrap();
                navigate('/users/groups');
            } else {
                const createdGroup = await createGroup(formData).unwrap();
                navigate(`/users/groups/edit/${createdGroup.id}`);
            }
        } catch (err) {
            setErrorMessage('Error al guardar el grupo');
            console.error(err);
        }
    };

  const handleAddUser = async (userId) => {
    try {
      await addUserToGroup({ groupId: id, userId }).unwrap();
      
      const [assignedResponse, availableResponse] = await Promise.all([
        getGroupUsers({ 
          groupId: id, 
          search: searchAssigned, 
          assigned: true 
        }).unwrap(),
        getGroupUsers({ 
          groupId: id, 
          search: searchAvailable, 
          assigned: false 
        }).unwrap()
      ]);

      setAssignedUsers(assignedResponse.groups || []);
      setAvailableUsers(availableResponse.groups || []);
    } catch (err) {
      setErrorMessage('Error al agregar el usuario.');
      console.error(err);
    }
  };

  const handleRemoveUser = async (userId) => {
    try {
      await removeUserFromGroup({ groupId: id, userId }).unwrap();

      const [assignedResponse, availableResponse] = await Promise.all([
        getGroupUsers({ 
          groupId: id, 
          search: searchAssigned, 
          assigned: true 
        }).unwrap(),
        getGroupUsers({ 
          groupId: id, 
          search: searchAvailable, 
          assigned: false 
        }).unwrap()
      ]);

      setAssignedUsers(assignedResponse.groups || []);
      setAvailableUsers(availableResponse.groups || []);
    } catch (err) {
      setErrorMessage('Error al quitar el usuario.');
      console.error(err);
    }
  };
  
    const handleGoBack = () => navigate('/users/groups');

    if (isGroupLoading && isEditing) {
        return <div className="p-5">Cargando grupo...</div>;
    }

    return (
        <div className="p-5">
            <div className="flex justify-between items-start mb-6">
                <h1 className="text-2xl font-bold">
                    {isEditing ? 'Editar Grupo' : 'Crear Grupo'}
                </h1>
                
                {isEditing && (
                    <div className='px-4 py-4 bg-gray-50 rounded-2xl flex gap-3 items-center'>
                        <Switch
                            checked={activeStatus}
                            onChange={handleStatusChange}
                            color="green"
                        />
                        <Typography variant='small' className='text-gray-500 font-semibold'>
                            {activeStatus ? 'Activo' : 'Inactivo'}
                        </Typography>
                    </div>
                )}
            </div>

            <GroupForm
                formData={formData}
                handleChange={handleChange}
                isEditing={isEditing}
                errorMessage={errorMessage}
            />
            
            {isEditing && (
                <>
                    <UserAssignment
                        assignedUsers={assignedUsers}
                        availableUsers={availableUsers}
                        handleAddUser={handleAddUser}
                        handleRemoveUser={handleRemoveUser}
                    />
                    
                    <div className="mt-12">
                        <h2 className="text-2xl font-bold mb-8">Gestión de Accesos</h2>
                        <div className="flex gap-6 min-h-[500px]">
                            <PermissionEntitiesSidebar
                                selectedEntity={selectedEntity}
                                onSelect={setSelectedEntity}
                                entities={ENTITY_MAP}
                            />
                            
                            <div className="flex-1">
                                {permissionEntities.find(e => e.type === selectedEntity)?.component}
                            </div>
                        </div>
                    </div>
                </>
            )}

            <div className="mt-8 flex space-x-4 justify-end">
                <Button
                    onClick={handleGoBack}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3"
                >
                    Volver
                </Button>
                <Button
                    onClick={handleSubmit}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3"
                >
                    {isEditing ? 'Actualizar' : 'Crear'}
                </Button>
            </div>
        </div>
    );
};

export default GroupDetail;